import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useStory } from "@/hooks/use-baseline";
import { StatusBadge } from "./triage-badge";

interface MatchedStorySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storyId: string | null;
  solutionId: string;
  /** The active triage item id, so we can highlight it in the evidence list. */
  highlightItemId?: string;
}

export function MatchedStorySheet({
  open,
  onOpenChange,
  storyId,
  solutionId,
  highlightItemId,
}: MatchedStorySheetProps) {
  const { data, isLoading } = useStory(storyId);

  const validatedCount =
    data?.matchedItems.filter((m) => m.effectiveStatus === "in-scope").length ?? 0;
  const discoveryCount = data?.matchedItems.length ?? 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[560px] flex flex-col">
        {isLoading && (
          <div className="p-6 space-y-3">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-7 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-32 w-full mt-4" />
          </div>
        )}

        {data && (
          <>
            <SheetHeader className="border-b border-[var(--color-border)]">
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold inline-flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" />
                Baseline · already in scope
              </div>
              <Breadcrumb
                deliverableCode={data.deliverableCode}
                deliverableTitle={data.deliverableTitle}
                epicCode={data.epicCode}
                epicTitle={data.epicTitle}
              />
              <SheetDescription className="sr-only">Mapped baseline story</SheetDescription>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              <section>
                <div className="font-mono text-sm text-muted-foreground tabular-nums mb-1">
                  {data.code}
                </div>
                <SheetTitle className="font-display text-lg leading-snug font-semibold">
                  {data.label?.trim() || data.text}
                </SheetTitle>
                {data.label?.trim() && (
                  <p className="text-sm text-foreground/75 mt-2">{data.text}</p>
                )}
              </section>

              <div className="grid grid-cols-2 gap-3">
                <Stat label="discovery items" value={discoveryCount} />
                <Stat label="validated in scope" value={validatedCount} />
              </div>

              {data.acceptanceCriteria.length > 0 && (
                <section>
                  <h3 className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-2">
                    Acceptance criteria
                  </h3>
                  <ol className="space-y-1.5">
                    {data.acceptanceCriteria.map((ac, i) => (
                      <li
                        key={ac.id}
                        className="flex gap-2 text-sm text-foreground/85 leading-snug"
                      >
                        <span className="font-mono text-xs text-muted-foreground tabular-nums w-5 shrink-0">
                          {i + 1}.
                        </span>
                        <span>{ac.text}</span>
                      </li>
                    ))}
                  </ol>
                </section>
              )}

              <section>
                <h3 className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-2">
                  Evidence from discovery
                </h3>
                {data.matchedItems.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No discovery items mapped yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.matchedItems.map((m) => {
                      const isCurrent = m.id === highlightItemId;
                      return (
                        <li
                          key={m.id}
                          className={
                            isCurrent
                              ? "rounded-md border-2 border-nymbl-blue bg-nymbl-sky/30 p-3"
                              : "rounded-md border border-[var(--color-border)] p-3"
                          }
                        >
                          <p className="text-sm text-foreground/85 line-clamp-2">{m.extracted}</p>
                          <div className="flex items-center gap-2 mt-1.5 text-[11px] text-muted-foreground">
                            <span className="font-mono tabular-nums">
                              {m.externalKey ?? m.id.slice(0, 6)}
                            </span>
                            {isCurrent && (
                              <span className="text-[10px] uppercase tracking-[0.14em] font-semibold text-nymbl-blue">
                                · current
                              </span>
                            )}
                            <span className="ml-auto">
                              <StatusBadge
                                status={
                                  m.effectiveStatus === m.suggestedStatus
                                    ? "pending"
                                    : m.effectiveStatus
                                }
                              />
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>

            <SheetFooter className="border-t border-[var(--color-border)] flex flex-row sm:justify-end gap-2 p-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button asChild>
                <Link
                  to="/s/$solutionId/scope/$storyId"
                  params={{ solutionId, storyId: data.id }}
                  onClick={() => onOpenChange(false)}
                >
                  Open in Scope
                  <ArrowUpRight className="h-3.5 w-3.5 ml-1.5" />
                </Link>
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Breadcrumb({
  deliverableCode,
  deliverableTitle,
  epicCode,
  epicTitle,
}: {
  deliverableCode: string | null;
  deliverableTitle: string | null;
  epicCode: string | null;
  epicTitle: string | null;
}) {
  return (
    <ol className="flex items-center gap-2 text-xs flex-wrap mt-1">
      {deliverableCode && (
        <li className="inline-flex items-center gap-1.5">
          <span className="font-mono tabular-nums text-foreground/80 bg-muted rounded px-1.5 py-0.5 text-[11px] font-semibold">
            {deliverableCode}
          </span>
          <span className="text-foreground">{deliverableTitle}</span>
        </li>
      )}
      {epicCode && (
        <>
          <li aria-hidden className="text-muted-foreground">
            ›
          </li>
          <li className="inline-flex items-center gap-1.5">
            <span className="font-mono tabular-nums text-foreground/80 bg-muted rounded px-1.5 py-0.5 text-[11px] font-semibold">
              {epicCode}
            </span>
            <span className="text-foreground">{epicTitle}</span>
          </li>
        </>
      )}
    </ol>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] p-3 bg-card">
      <div className="font-display text-2xl font-bold text-foreground tabular-nums leading-none">
        {value}
      </div>
      <div className="text-[11px] text-muted-foreground mt-1">{label}</div>
    </div>
  );
}
